// Inside-the-Canvas half of the cinematic player: implements the
// CinematicCamera interface on the real camera + OrbitControls and registers
// it for the director, and adds the warm "golden hour" key light the
// recipes' timeOfDay('golden') fades in. Mounted once by Scene.

import { useFrame, useThree } from '@react-three/fiber';
import { type RefObject, useEffect, useRef } from 'react';
import type * as THREE from 'three';
import { getBoardScale } from '../render/boardScale.js';
import { type CinematicCamera, registerCinematicCamera } from './cameraBridge.js';
import type { CameraPose } from './types.js';

/** The slice of drei's OrbitControls (three-stdlib) the rig needs. */
export interface OrbitControlsLike {
  readonly target: THREE.Vector3;
  enabled: boolean;
  update(): boolean;
}

/** Enough update() calls for enableDamping's leftover velocity to decay to nothing. */
const SETTLE_UPDATES = 240;

export function CinematicCameraRig({
  controls,
}: {
  readonly controls: RefObject<OrbitControlsLike | null>;
}) {
  const camera = useThree((s) => s.camera);
  const golden = useRef(0);
  const keyRef = useRef<THREE.DirectionalLight>(null);
  const fillRef = useRef<THREE.AmbientLight>(null);

  useEffect(() => {
    // Cinematic timelines are authored in board-space units; the rendered
    // board may be scaled (Large Board). getPose inverts the scale so the
    // setActive round-trip (get → apply) is exact.
    const applyPose = (pose: CameraPose) => {
      const s = getBoardScale();
      const [px, py, pz] = pose.position;
      const [tx, ty, tz] = pose.target;
      camera.position.set(px * s, py * s, pz * s);
      controls.current?.target.set(tx * s, ty * s, tz * s);
      camera.lookAt(tx * s, ty * s, tz * s);
      camera.updateMatrixWorld();
    };
    const impl: CinematicCamera = {
      getPose: () => {
        const inv = 1 / getBoardScale();
        const tgt = controls.current?.target;
        return {
          position: [camera.position.x * inv, camera.position.y * inv, camera.position.z * inv],
          target: tgt ? [tgt.x * inv, tgt.y * inv, tgt.z * inv] : [0, 0, 0],
        };
      },
      setPose: applyPose,
      setActive: (active) => {
        const ctl = controls.current;
        if (!ctl) return;
        if (active) {
          ctl.enabled = false;
          return;
        }
        // Drain whatever damping velocity the player's last drag left in the
        // controls, then pin the pose again so the hand-back is exact.
        const pose = impl.getPose();
        ctl.enabled = true;
        for (let i = 0; i < SETTLE_UPDATES; i++) ctl.update();
        applyPose(pose);
        ctl.update();
      },
      setGolden: (amount) => {
        golden.current = Math.min(1, Math.max(0, amount));
      },
    };
    return registerCinematicCamera(impl);
  }, [camera, controls]);

  useFrame(() => {
    const g = golden.current;
    if (keyRef.current) keyRef.current.intensity = 2.2 * g;
    if (fillRef.current) fillRef.current.intensity = 0.35 * g;
  });

  return (
    <>
      {/* Low, warm sun from the west: long golden light across the tiles. */}
      <directionalLight ref={keyRef} color="#ffb25e" position={[-30, 9, 14]} intensity={0} />
      <ambientLight ref={fillRef} color="#ffcf8f" intensity={0} />
    </>
  );
}
